/**
 * Simulator Dashboard Component
 * Real-time display of simulation progress and metrics
 * Enhanced visualization with activity feed and animated stats
 */

import React, { useMemo } from 'react';
import {
  Activity, Clock, Zap, AlertCircle, CheckCircle, Package,
  Truck, Box, ClipboardList, MapPin, RefreshCw, Users, TrendingUp,
  TrendingDown, Minus, Play, Pause, Timer, BarChart3, Edit3, ShoppingCart
} from 'lucide-react';
import { SimulatorState, WorkflowType, VirtualOperatorInfo, WorkflowMetrics } from './types';

interface SimulatorDashboardProps {
  state: SimulatorState;
}

export const SimulatorDashboard: React.FC<SimulatorDashboardProps> = ({ state }) => {
  const { metrics, operators, status } = state;

  const formatTime = (seconds: number): string => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate totals
  const totalSuccess = useMemo(() =>
    Object.values(metrics.workflows).reduce((sum, w) => sum + w.successCount, 0),
    [metrics.workflows]
  );

  const totalErrors = useMemo(() =>
    Object.values(metrics.workflows).reduce((sum, w) => sum + w.errorCount, 0),
    [metrics.workflows]
  );

  const activeOperators = operators.filter(o => o.status === 'busy').length;

  return (
    <div className="space-y-6">
      {/* Hero Stats Banner */}
      <div className={`rounded-xl shadow-xl overflow-hidden ${
        status === 'running'
          ? 'bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600'
          : 'bg-gradient-to-r from-yellow-600 via-amber-600 to-orange-600'
      }`}>
        <div className="px-6 py-5">
          <div className="flex items-center justify-between">
            {/* Status Indicator */}
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                  status === 'running' ? 'bg-white/20' : 'bg-white/20'
                }`}>
                  {status === 'running' ? (
                    <Play className="w-8 h-8 text-white fill-white" />
                  ) : (
                    <Pause className="w-8 h-8 text-white" />
                  )}
                </div>
                {status === 'running' && (
                  <span className="absolute -top-1 -right-1 flex h-4 w-4">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-4 w-4 bg-white"></span>
                  </span>
                )}
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">
                  {status === 'running' ? 'Simulering Aktiv' : 'Simulering Pauset'}
                </h2>
                <p className="text-white/70 text-sm mt-1">
                  {activeOperators} av {operators.length} operatorer aktive
                </p>
              </div>
            </div>

            {/* Main Stats */}
            <div className="flex items-center gap-8">
              <StatBox icon={Timer} label="Kjøretid" value={formatTime(metrics.elapsedTime)} />
              <StatBox icon={BarChart3} label="Transaksjoner" value={metrics.totalTransactions.toLocaleString()} />
              <StatBox icon={TrendingUp} label="Req/sek" value={metrics.requestsPerSecond.toFixed(1)} />
              <StatBox
                icon={metrics.errorRate > 5 ? TrendingDown : CheckCircle}
                label="Feilrate"
                value={`${metrics.errorRate.toFixed(1)}%`}
                highlight={metrics.errorRate > 5 ? 'error' : 'success'}
              />
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="h-2 bg-black/20">
          <div
            className="h-full bg-white/40 transition-all duration-1000 ease-out"
            style={{
              width: `${Math.min(100, (metrics.totalTransactions / state.config.safety.maxTransactionsPerSession) * 100)}%`
            }}
          />
        </div>
      </div>

      {/* Error Banner */}
      {state.errors && state.errors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-700">Simulering-problemer:</p>
            <ul className="text-sm text-red-600 mt-1 space-y-0.5">
              {Array.from(new Set(state.errors)).slice(-5).map((err, i) => (
                <li key={i}>{err}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Batch/Serial Status Badge */}
      {state.config.batchSerialConfig?.enabled && (
        <div className="flex items-center gap-2 px-4 py-2 bg-teal-50 border border-teal-200 rounded-lg text-sm text-teal-700">
          <Package className="w-4 h-4" />
          <span className="font-medium">Batch/Serial sporing aktiv</span>
          <span className="text-teal-500">— ItemType 4/5 (batch) og 6 (serial) inkluderes i transaksjoner</span>
        </div>
      )}

      {/* Workflow Performance Grid */}
      <div className="grid grid-cols-4 gap-4">
        <WorkflowCard
          type="picking"
          label="Plukking"
          icon={Package}
          metrics={metrics.workflows.picking}
          enabled={state.config.workflows.picking.enabled}
          color="blue"
        />
        <WorkflowCard
          type="packing"
          label="Pakking"
          icon={Box}
          metrics={metrics.workflows.packing}
          enabled={state.config.workflows.packing.enabled}
          color="purple"
        />
        <WorkflowCard
          type="receiving"
          label="Mottak"
          icon={Truck}
          metrics={metrics.workflows.receiving}
          enabled={state.config.workflows.receiving.enabled}
          color="green"
        />
        <WorkflowCard
          type="putaway"
          label="Put-Away"
          icon={MapPin}
          metrics={metrics.workflows.putaway}
          enabled={state.config.workflows.putaway.enabled}
          color="orange"
        />
        <WorkflowCard
          type="counting"
          label="Telling"
          icon={ClipboardList}
          metrics={metrics.workflows.counting}
          enabled={state.config.workflows.counting.enabled}
          color="cyan"
        />
        <WorkflowCard
          type="transfer"
          label="Transfer"
          icon={RefreshCw}
          metrics={metrics.workflows.transfer}
          enabled={state.config.workflows.transfer.enabled}
          color="pink"
        />
        <WorkflowCard
          type="correction"
          label="Korreksjon"
          icon={Edit3}
          metrics={metrics.workflows.correction}
          enabled={state.config.workflows.correction.enabled}
          color="indigo"
        />
        <WorkflowCard
          type="posdeduction"
          label="POS Fradrag"
          icon={ShoppingCart}
          metrics={metrics.workflows.posdeduction}
          enabled={state.config.workflows.posdeduction.enabled}
          color="rose"
        />
      </div>

      {/* Operators and Activity */}
      <div className="grid grid-cols-3 gap-6">
        {/* Operators Panel */}
        <div className="col-span-2 bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="px-5 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5 text-white" />
              <h3 className="font-semibold text-white">Virtuelle Operatorer</h3>
            </div>
            <span className="px-3 py-1 bg-white/20 rounded-full text-sm text-white">
              {activeOperators} / {operators.length} aktive
            </span>
          </div>

          <div className="p-4">
            <div className="grid grid-cols-2 gap-3">
              {operators.map(operator => (
                <OperatorCard key={operator.id} operator={operator} />
              ))}
            </div>
          </div>
        </div>

        {/* Performance Panel */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="px-5 py-4 bg-gradient-to-r from-purple-600 to-pink-600 flex items-center gap-3">
            <Activity className="w-5 h-5 text-white" />
            <h3 className="font-semibold text-white">System-ytelse</h3>
          </div>

          <div className="p-4 space-y-4">
            <PerformanceMetric
              label="API Throughput"
              value={`${metrics.requestsPerSecond.toFixed(1)} / 5.0 req/s`}
              percentage={Math.min(100, (metrics.requestsPerSecond / 5) * 100)}
              color="blue"
            />
            <PerformanceMetric
              label="Avg. Responstid"
              value={`${metrics.averageResponseTime.toFixed(0)}ms`}
              percentage={Math.min(100, (metrics.averageResponseTime / 1000) * 100)}
              color={metrics.averageResponseTime < 200 ? 'green' : metrics.averageResponseTime < 500 ? 'yellow' : 'red'}
            />
            <PerformanceMetric
              label="Feilrate"
              value={`${metrics.errorRate.toFixed(2)}%`}
              percentage={Math.min(100, metrics.errorRate * 10)}
              color={metrics.errorRate < 2 ? 'green' : metrics.errorRate < 5 ? 'yellow' : 'red'}
            />
            <PerformanceMetric
              label="Aktive Låser"
              value={metrics.lockedPLists.toString()}
              percentage={Math.min(100, (metrics.lockedPLists / 10) * 100)}
              color="purple"
            />

            {/* Summary Stats */}
            <div className="pt-4 border-t border-gray-100">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-green-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-600">{totalSuccess}</p>
                  <p className="text-xs text-green-700">Vellykkede</p>
                </div>
                <div className="p-3 bg-red-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-red-600">{totalErrors}</p>
                  <p className="text-xs text-red-700">Feil</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-5 flex items-center gap-4">
        <div className="p-3 bg-blue-100 rounded-lg">
          <Zap className="w-6 h-6 text-blue-600" />
        </div>
        <div>
          <h4 className="font-semibold text-blue-800">Sanntids-observasjon tilgjengelig</h4>
          <p className="text-sm text-blue-700 mt-1">
            Andre brukere kan logge inn og se endringene i sanntid via de vanlige modulene
            (plukk, pakk, mottak, etc.). Alle transaksjoner oppdaterer ekte data i databasen.
          </p>
        </div>
      </div>
    </div>
  );
};

// Stat Box for Hero Section
interface StatBoxProps {
  icon: React.FC<{ className?: string }>;
  label: string;
  value: string;
  highlight?: 'success' | 'error';
}

const StatBox: React.FC<StatBoxProps> = ({ icon: Icon, label, value, highlight }) => (
  <div className="text-center">
    <div className={`flex items-center justify-center gap-2 ${
      highlight === 'error' ? 'text-red-200' : 'text-white'
    }`}>
      <Icon className="w-5 h-5" />
      <span className="text-3xl font-bold">{value}</span>
    </div>
    <p className="text-white/70 text-sm mt-1">{label}</p>
  </div>
);

// Workflow Card Component
interface WorkflowCardProps {
  type: WorkflowType;
  label: string;
  icon: React.FC<{ className?: string }>;
  metrics: WorkflowMetrics;
  enabled: boolean;
  color: 'blue' | 'purple' | 'green' | 'orange' | 'cyan' | 'pink' | 'indigo' | 'rose';
}

const WorkflowCard: React.FC<WorkflowCardProps> = ({
  label,
  icon: Icon,
  metrics,
  enabled,
  color
}) => {
  const colorMap = {
    blue: { bg: 'from-blue-500 to-blue-600', light: 'bg-blue-100', text: 'text-blue-600' },
    purple: { bg: 'from-purple-500 to-purple-600', light: 'bg-purple-100', text: 'text-purple-600' },
    green: { bg: 'from-green-500 to-green-600', light: 'bg-green-100', text: 'text-green-600' },
    orange: { bg: 'from-orange-500 to-orange-600', light: 'bg-orange-100', text: 'text-orange-600' },
    cyan: { bg: 'from-cyan-500 to-cyan-600', light: 'bg-cyan-100', text: 'text-cyan-600' },
    pink: { bg: 'from-pink-500 to-pink-600', light: 'bg-pink-100', text: 'text-pink-600' },
    indigo: { bg: 'from-indigo-500 to-indigo-600', light: 'bg-indigo-100', text: 'text-indigo-600' },
    rose: { bg: 'from-rose-500 to-rose-600', light: 'bg-rose-100', text: 'text-rose-600' }
  };

  const colors = colorMap[color];
  const total = metrics.successCount + metrics.errorCount;
  const successRate = total > 0 ? (metrics.successCount / total) * 100 : 100;

  return (
    <div className={`bg-white rounded-xl shadow-lg overflow-hidden ${!enabled && 'opacity-50 grayscale'}`}>
      <div className={`h-2 bg-gradient-to-r ${colors.bg}`} />
      <div className="p-4 text-center">
        <div className={`w-12 h-12 mx-auto mb-3 rounded-xl ${colors.light} flex items-center justify-center`}>
          <Icon className={`w-6 h-6 ${colors.text}`} />
        </div>
        <p className="text-sm font-medium text-gray-700 mb-2">{label}</p>

        {enabled ? (
          <>
            <p className="text-2xl font-bold text-gray-800">{metrics.successCount}</p>
            <p className="text-xs text-gray-500 mb-2">fullfort</p>

            {/* Mini progress bar showing success rate */}
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full bg-gradient-to-r ${colors.bg} transition-all duration-500`}
                style={{ width: `${successRate}%` }}
              />
            </div>

            {metrics.errorCount > 0 && (
              <p className="text-xs text-red-500 mt-2">{metrics.errorCount} feil</p>
            )}
          </>
        ) : (
          <span className="text-xs text-gray-400">Deaktivert</span>
        )}
      </div>
    </div>
  );
};

// Operator Card Component
interface OperatorCardProps {
  operator: VirtualOperatorInfo;
}

const OperatorCard: React.FC<OperatorCardProps> = ({ operator }) => {
  const statusConfig = {
    idle: { bg: 'bg-gray-50', border: 'border-gray-200', dot: 'bg-gray-400', text: 'Ledig' },
    busy: { bg: 'bg-green-50', border: 'border-green-200', dot: 'bg-green-500', text: 'Aktiv' },
    error: { bg: 'bg-red-50', border: 'border-red-200', dot: 'bg-red-500', text: 'Feil' }
  };

  const config = statusConfig[operator.status];

  const workflowIcons: Record<string, React.FC<{ className?: string }>> = {
    picking: Package,
    packing: Box,
    receiving: Truck,
    putaway: MapPin,
    counting: ClipboardList,
    transfer: RefreshCw,
    correction: Edit3,
    posdeduction: ShoppingCart
  };

  const WorkflowIcon = operator.currentWorkflow
    ? workflowIcons[operator.currentWorkflow]
    : null;

  return (
    <div className={`p-3 rounded-lg border ${config.bg} ${config.border} transition-all`}>
      <div className="flex items-center gap-3">
        <div className="relative">
          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-sm font-medium text-gray-600">
            {operator.name.split('-')[1] || operator.id.slice(-2)}
          </div>
          <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full ${config.dot} border-2 border-white`} />
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-800 truncate">{operator.name}</p>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            {operator.status === 'busy' && WorkflowIcon && (
              <>
                <WorkflowIcon className="w-3 h-3" />
                <span className="truncate">{operator.currentTask}</span>
              </>
            )}
            {operator.status === 'idle' && <span>{config.text}</span>}
            {operator.status === 'error' && <span className="text-red-500">{config.text}</span>}
          </div>
        </div>

        <div className="text-right">
          <p className="text-sm font-bold text-gray-700">{operator.tasksCompleted}</p>
          <p className="text-xs text-gray-500">oppgaver</p>
        </div>
      </div>
    </div>
  );
};

// Performance Metric Component
interface PerformanceMetricProps {
  label: string;
  value: string;
  percentage: number;
  color: 'blue' | 'green' | 'yellow' | 'red' | 'purple';
}

const PerformanceMetric: React.FC<PerformanceMetricProps> = ({
  label,
  value,
  percentage,
  color
}) => {
  const colorMap = {
    blue: 'from-blue-500 to-blue-600',
    green: 'from-green-500 to-green-600',
    yellow: 'from-yellow-500 to-yellow-600',
    red: 'from-red-500 to-red-600',
    purple: 'from-purple-500 to-purple-600'
  };

  return (
    <div>
      <div className="flex justify-between text-sm mb-1.5">
        <span className="text-gray-600">{label}</span>
        <span className="font-medium text-gray-800">{value}</span>
      </div>
      <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full bg-gradient-to-r ${colorMap[color]} transition-all duration-500 ease-out rounded-full`}
          style={{ width: `${Math.max(2, percentage)}%` }}
        />
      </div>
    </div>
  );
};

export default SimulatorDashboard;
